import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Activity,
  BatteryFull,
  Box,
  Cpu,
  DoorClosed,
  Lock,
  PlugZap,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Unlock,
} from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { useToast } from '../components/Toast.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { Modal } from '../components/Modal.tsx';
import { Reveal, Stagger, StaggerItem } from '../components/motion.tsx';
import { CountUp } from '../components/CountUp.tsx';
import type { RouterOutputs } from '@cdvi-edge/api';
import './ControlPanel.css';

type ControlPanelData = RouterOutputs['controller']['controlPanelState'];
type Door = ControlPanelData['doors'][number];
type Zone = ControlPanelData['zones'][number];

export function ControlPanel() {
  const { id } = useParams<{ id: string }>();

  const stateQuery = trpc.controller.controlPanelState.useQuery(
    { id: id! },
    { enabled: !!id, refetchInterval: 1500 },
  );

  if (!id) return <div className="empty">Missing controller id.</div>;

  if (stateQuery.isLoading) {
    return (
      <div className="cp">
        <div className="skel" style={{ height: 30, width: 240, marginBottom: 18 }} />
        <div className="cp-health">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skel" style={{ height: 52, borderRadius: 'var(--r-lg)' }} />
          ))}
        </div>
        <div className="grid grid-2" style={{ marginTop: 24 }}>
          {[0, 1].map((i) => (
            <div key={i} className="skel" style={{ height: 132, borderRadius: 'var(--r-lg)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (stateQuery.error) {
    return (
      <div className="empty">
        <div className="icon" style={{ color: 'var(--danger)' }}>
          <ShieldAlert size={22} />
        </div>
        <div className="danger-text" style={{ fontWeight: 600 }}>
          Control panel unavailable
        </div>
        <div className="dim" style={{ marginTop: 4 }}>{stateQuery.error.message}</div>
      </div>
    );
  }

  const d = stateQuery.data!;

  return (
    <div className="cp">
      <Reveal>
        <header className="cp-header">
          <div className="cp-title">
            <div className="section-eyebrow">Live control room</div>
            <h1>{d.controller.name}</h1>
          </div>
          <div className="row" style={{ gap: '0.6rem' }}>
            <span className={`badge ${d.controller.isOnline ? 'ok' : 'danger'}`}>
              {d.controller.isOnline && <span className="live-dot" aria-hidden />}
              {d.controller.isOnline ? 'Online' : 'Offline'}
            </span>
            {d.snapshotAgeSeconds !== null && (
              <span className="cp-snapshot mono">
                <Activity size={12} aria-hidden />
                snapshot&nbsp;
                <CountUp value={d.snapshotAgeSeconds} suffix="s" duration={500} />
                &nbsp;ago
              </span>
            )}
          </div>
        </header>
      </Reveal>

      <Reveal delay={0.05}>
        <section className="cp-section" aria-label="Controller health">
          <Stagger className="cp-health">
            <StaggerItem>
              <HealthPill label="AC power" icon={<PlugZap size={15} />} ok={d.controller.acPowerOk} />
            </StaggerItem>
            <StaggerItem>
              <HealthPill label="Battery" icon={<BatteryFull size={15} />} ok={d.controller.batteryOk} />
            </StaggerItem>
            <StaggerItem>
              <HealthPill label="Box tamper" icon={<Box size={15} />} ok={d.controller.boxTamperOk} />
            </StaggerItem>
            <StaggerItem>
              <HealthPill label="Wall tamper" icon={<ShieldCheck size={15} />} ok={d.controller.wallTamperOk} />
            </StaggerItem>
            <StaggerItem>
              <div className="cp-pill cp-pill-fw">
                <span className="cp-pill-icon">
                  <Cpu size={15} />
                </span>
                <div className="cp-pill-text">
                  <span className="cp-pill-label">Firmware</span>
                  <span className="cp-pill-state mono">
                    {d.health?.firmwareVersion ?? d.controller.firmwareVersion ?? '—'}
                  </span>
                </div>
              </div>
            </StaggerItem>
          </Stagger>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="cp-section" aria-label="Doors">
          <div className="cp-section-head">
            <h2>Doors</h2>
            <span className="dim mono">{d.doors.length} mapped</span>
          </div>
          {d.doors.length === 0 ? (
            <div className="empty">
              <div className="icon">
                <DoorClosed size={22} />
              </div>
              No doors mapped to this controller yet.
            </div>
          ) : (
            <Stagger className="grid grid-2">
              {d.doors.map((door) => (
                <StaggerItem key={door.id}>
                  <DoorTile door={door} controllerId={id} />
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </section>
      </Reveal>

      <Reveal delay={0.15}>
        <section className="cp-section" aria-label="Zones">
          <div className="cp-section-head">
            <h2>Zones</h2>
            <span className="dim mono">{d.zones.length} monitored</span>
          </div>
          {d.zones.length === 0 ? (
            <div className="empty">No zones reported.</div>
          ) : (
            <ul className="cp-zones">
              {d.zones.map((z) => (
                <ZoneRow key={z.id} zone={z} />
              ))}
            </ul>
          )}
        </section>
      </Reveal>
    </div>
  );
}

function HealthPill({
  label,
  icon,
  ok,
}: {
  label: string;
  icon: ReactNode;
  ok: boolean | null | undefined;
}) {
  const state = ok === null || ok === undefined ? 'unknown' : ok ? 'ok' : 'fault';
  const stateLabel = state === 'ok' ? 'Nominal' : state === 'fault' ? 'Fault' : 'No data';
  return (
    <div className={`cp-pill cp-pill-${state}`}>
      <span className="cp-pill-icon">{icon}</span>
      <div className="cp-pill-text">
        <span className="cp-pill-label">{label}</span>
        <span className="cp-pill-state">
          <span className="cp-pill-dot" aria-hidden />
          {stateLabel}
        </span>
      </div>
    </div>
  );
}

/** Maps a live snapshot lock state to a semantic badge tone + display label. */
function lockTone(state: string): { tone: string; label: string } {
  switch (state) {
    case 'locked':
      return { tone: 'ok', label: 'Locked' };
    case 'unlocked':
      return { tone: 'gold', label: 'Unlocked' };
    case 'forced':
      return { tone: 'danger', label: 'Forced' };
    case 'held':
      return { tone: 'warn', label: 'Held open' };
    default:
      return { tone: 'neutral', label: 'Unknown' };
  }
}

function DoorTile({ door, controllerId }: { door: Door; controllerId: string }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const utils = trpc.useUtils();
  const invalidate = () => utils.controller.controlPanelState.invalidate({ id: controllerId });

  const unlock = trpc.door.unlock.useMutation({
    onSuccess: () => {
      toast.success('Unlock sent', door.name);
      invalidate();
    },
    onError: (e) => toast.error('Unlock failed', e.message),
  });

  const lockState = door.snapshot?.lockState ?? 'unknown';
  const { tone, label } = lockTone(lockState);
  const isLockdown = door.isLockdown;
  const remaining = door.snapshot?.unlockRemainingSeconds ?? 0;
  const live = lockState === 'unlocked';

  return (
    <>
      <div className={`card cp-door ${isLockdown ? 'cp-door-lockdown' : ''}`}>
        <div className="cp-door-head">
          <span className={`cp-door-icon ${isLockdown ? 'is-lockdown' : `is-${tone}`}`}>
            {isLockdown ? <Lock size={18} /> : <DoorClosed size={18} />}
          </span>
          <div className="cp-door-name">
            <strong>{door.name}</strong>
            <span className="dim mono">unlock {door.unlockTimeSeconds}s</span>
          </div>
          <span className={`badge ${isLockdown ? 'danger' : tone === 'neutral' ? '' : tone}`}>
            {live && <span className="live-dot" aria-hidden />}
            {isLockdown ? 'Lockdown' : label}
            {live && remaining > 0 ? ` · ${remaining}s` : ''}
          </span>
        </div>

        <div className="cp-door-actions">
          <button
            disabled={unlock.isPending || isLockdown}
            onClick={() => unlock.mutate({ doorId: door.id, durationSeconds: door.unlockTimeSeconds })}
            title={isLockdown ? 'Release lockdown before unlocking' : 'Pulse unlock'}
          >
            <Unlock size={14} />
            {unlock.isPending ? 'Sending…' : 'Unlock'}
          </button>
          <button className="secondary" onClick={() => setOpen(true)}>
            <SlidersHorizontal size={14} />
            Controls
          </button>
        </div>
      </div>

      <DoorControlsModal
        door={door}
        controllerId={controllerId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function DoorControlsModal({
  door,
  controllerId,
  open,
  onClose,
}: {
  door: Door;
  controllerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const utils = trpc.useUtils();
  const invalidate = () => utils.controller.controlPanelState.invalidate({ id: controllerId });
  const [duration, setDuration] = useState(door.unlockTimeSeconds);

  const unlock = trpc.door.unlock.useMutation({
    onSuccess: () => {
      toast.success('Unlock sent', `${door.name} · ${duration}s`);
      invalidate();
    },
    onError: (e) => toast.error('Unlock failed', e.message),
  });
  const lockdown = trpc.door.lockdown.useMutation({
    onSuccess: () => {
      toast.warn('Lockdown engaged', door.name);
      invalidate();
    },
    onError: (e) => toast.error('Lockdown failed', e.message),
  });
  const release = trpc.door.releaseLockdown.useMutation({
    onSuccess: () => {
      toast.success('Lockdown released', door.name);
      invalidate();
    },
    onError: (e) => toast.error('Release failed', e.message),
  });
  const normal = trpc.door.normal.useMutation({
    onSuccess: () => {
      toast.success('Returned to normal', door.name);
      invalidate();
    },
    onError: (e) => toast.error('Normal failed', e.message),
  });

  const busy = unlock.isPending || lockdown.isPending || release.isPending || normal.isPending;
  const lockState = door.snapshot?.lockState ?? 'unknown';
  const { tone, label } = lockTone(lockState);

  async function onLockdown() {
    const ok = await confirm({
      title: `Engage lockdown on ${door.name}?`,
      body: 'This immediately denies all access at this door until lockdown is released.',
      confirmLabel: 'Engage lockdown',
      danger: true,
    });
    if (ok) lockdown.mutate({ doorId: door.id });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissable={!busy}
      maxWidth={520}
      title={
        <span className="row" style={{ gap: 8 }}>
          {door.isLockdown ? <Lock size={18} className="err" /> : <DoorClosed size={18} className="gold" />}
          {door.name}
        </span>
      }
    >
      <div className="cp-modal-status">
        <span className="section-eyebrow">Current state</span>
        <span className={`badge ${door.isLockdown ? 'danger' : tone === 'neutral' ? '' : tone}`}>
          {door.isLockdown ? 'Lockdown' : label}
        </span>
      </div>

      <label className="cp-field">
        <span className="cp-field-label">Unlock duration</span>
        <div className="row" style={{ gap: '0.6rem' }}>
          <input
            type="number"
            min={1}
            max={300}
            value={duration}
            onChange={(e) => setDuration(Math.max(1, Math.min(300, Number(e.target.value) || 1)))}
            disabled={busy}
            aria-label="Unlock duration in seconds"
            style={{ width: 100 }}
          />
          <span className="dim mono">seconds</span>
        </div>
      </label>

      <div className="cp-modal-actions">
        <button
          disabled={busy || door.isLockdown}
          onClick={() => unlock.mutate({ doorId: door.id, durationSeconds: duration })}
          title={door.isLockdown ? 'Release lockdown before unlocking' : undefined}
        >
          <Unlock size={14} />
          Unlock {duration}s
        </button>

        {door.isLockdown ? (
          <button
            className="secondary"
            disabled={busy}
            onClick={() => release.mutate({ doorId: door.id })}
          >
            <Unlock size={14} />
            Release lockdown
          </button>
        ) : (
          <button className="danger" disabled={busy} onClick={() => void onLockdown()}>
            <ShieldAlert size={14} />
            Lockdown
          </button>
        )}

        <button
          className="ghost"
          disabled={busy}
          onClick={() => normal.mutate({ doorId: door.id })}
        >
          <ShieldCheck size={14} />
          Return to normal
        </button>
      </div>
    </Modal>
  );
}

/** Maps zone snapshot status to a semantic badge tone + display label. */
function zoneTone(status: string): { tone: string; label: string } {
  switch (status) {
    case 'normal':
      return { tone: 'ok', label: 'Normal' };
    case 'open':
      return { tone: 'warn', label: 'Open' };
    case 'short':
      return { tone: 'danger', label: 'Short' };
    case 'bypassed':
      return { tone: 'info', label: 'Bypassed' };
    default:
      return { tone: 'neutral', label: 'Unknown' };
  }
}

function ZoneRow({ zone }: { zone: Zone }) {
  const status = zone.snapshot?.status ?? 'unknown';
  const { tone, label } = zoneTone(status);
  return (
    <li className="cp-zone">
      <span className={`cp-zone-dot is-${tone}`} aria-hidden />
      <span className="cp-zone-name">{zone.name}</span>
      <span className={`badge ${tone === 'neutral' ? '' : tone}`}>{label}</span>
    </li>
  );
}
